import { useState } from "react";
import DraftClass from "./DraftClass.tsx";
import { EmptyState } from "../../components/EmptyState.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import type { View } from "../../../../../common/types.ts";
import {
  MobileArchiveSeasonNav,
  MobileContentHero,
  MobileEntityRow,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../../mobile/mobilePrimitives.tsx";
import { helpers } from "../../util/helpers.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { Trans, i18n } from "../../util/useT.ts";
import useIsMobileViewport from "../../hooks/useIsMobileViewport.ts";
import { getDraftScoutingDecisionContext } from "../draftStageTemplate.ts";
import { getScoutingConfidenceMetricScore } from "../marketEvaluationMetrics.ts";

const PAGE_SIZE = 3;

const DraftScouting = ({
  challengeNoRatings,
  currentSeason,
  draftType,
  fantasyDraft,
  requestedSeason,
  seasonContext,
  seasons,
}: View<"draftScouting">) => {
  const noDraft = draftType === "freeAgents";

  useTitleBar({
    title: !noDraft
      ? i18n.t("draftScouting.title", { ns: "ui", defaultValue: "Draft Scouting" })
      : i18n.t("draftScouting.upcomingProspects", { ns: "ui", defaultValue: "Upcoming Prospects" }),
  });

  const [page, setPage] = useState(0);
  const isMobileViewport = useIsMobileViewport();

  if (seasons.length <= PAGE_SIZE && page !== 0) {
    setPage(0);
  }

  const pagination = !isMobileViewport && seasons.length > PAGE_SIZE;
  const maxPage = Math.ceil(seasons.length / PAGE_SIZE) - 1;
  const enablePrevious = pagination && page > 0;
  const enableNext = pagination && page < maxPage;

  let seasonsToDisplay;
  if (isMobileViewport) {
    seasonsToDisplay = seasons.slice(0, 1);
  } else if (pagination) {
    const indexStart = page * PAGE_SIZE;
    const indexEnd = indexStart + PAGE_SIZE;
    seasonsToDisplay = seasons.slice(indexStart, indexEnd);
  } else {
    seasonsToDisplay = seasons;
  }

  const displayedSeasonInfo = seasonsToDisplay[0];
  const mobilePreview = getDraftScoutingDecisionContext({
    playerCount: displayedSeasonInfo?.players.length ?? 0,
    season: displayedSeasonInfo?.season,
  });
  const mobileShortlistPlayers = displayedSeasonInfo?.players.slice(0, 3) ?? [];
  const mobileScoutingCoverage = i18n.t("draftScouting.prospectsScoutedValue", {
    ns: "ui",
    count: mobilePreview.previewCount,
    defaultValue: "{{count}} of {{total}} prospects",
    total: displayedSeasonInfo?.players.length ?? 0,
  });
  const isHistoricalScouting = seasonContext === "history";
  const activeSeason = displayedSeasonInfo?.season ?? requestedSeason ?? currentSeason;
  const scoutingConfidenceLabel = (() => {
    switch (mobilePreview.confidence) {
      case "high":
        return i18n.t("draftScouting.p2v2ConfidenceHigh", {
          ns: "ui",
          defaultValue: "Watch list is set",
        });
      case "medium":
        return i18n.t("draftScouting.p2v2ConfidenceMedium", {
          ns: "ui",
          defaultValue: "Watch list still filling in",
        });
      case "low":
        return i18n.t("draftScouting.p2v2ConfidenceLow", {
          ns: "ui",
          defaultValue: "Only a few names so far",
        });
      default:
        return i18n.t("draftScouting.p2v2ConfidenceNone", {
          ns: "ui",
          defaultValue: "No prospects to scout yet",
        });
    }
  })();

  // G2d-1: v5Content migration. The legacy core-task / archive-readonly template
  // wrappers and the decision-summary "team needs shortlist" meta card
  // (conclusion/consequence/deadline narration, 法则一) are gone. The first screen
  // is the rookie-class content itself inside a single MobileContentHero: the top
  // prospects as MobileEntityRow rows and the class overview as MobileStatRow
  // rows. The full class DataTables stay in the expert disclosure.
  // G2d-1 fix: the page title lives on the TitleBar only (法则二, the "选秀侦察
  // ×3" browser finding). The hero keeps a title just for the historical
  // variant, where the season-specific board name adds real context.
  const heroTitle = isHistoricalScouting
    ? i18n.t("draftScouting.p2s06HistoricalRookieBoardTitle", {
        ns: "ui",
        season: activeSeason,
        defaultValue: "{{season}} historical rookie board",
      })
    : undefined;

  // The class summary is the single explainer line (法则一): what this class is
  // and where to start.
  const heroExplainer = isHistoricalScouting
    ? mobilePreview.previewCount > 0
      ? i18n.t("draftScouting.p2s03HistoryConclusion", {
          ns: "ui",
          count: mobilePreview.previewCount,
          season: activeSeason,
          defaultValue:
            "{{season}} is a read-only scouting archive with {{count}} prospects compared against later performance.",
        })
      : i18n.t("draftScouting.p2v2EmptyConclusion", {
          ns: "ui",
          season: mobilePreview.season,
          defaultValue:
            "No prospects are available for {{season}} yet, so keep the full class view as a later check.",
        })
    : mobilePreview.previewCount > 0
      ? i18n.t("draftScouting.p2v2Conclusion", {
          ns: "ui",
          count: mobilePreview.previewCount,
          recommended: mobilePreview.recommendedCount,
          season: mobilePreview.season,
          defaultValue:
            "The {{recommended}} names most worth watching in the {{season}} class are at the top.",
        })
      : i18n.t("draftScouting.p2v2EmptyConclusion", {
          ns: "ui",
          season: mobilePreview.season,
          defaultValue:
            "No prospects are available for {{season}} yet, so keep the full class view as a later check.",
        });

  const heroAction = (
    <a
      className={isHistoricalScouting ? "btn btn-outline-secondary" : "btn btn-primary"}
      href={
        isHistoricalScouting
          ? helpers.leagueUrl(["draft_history", activeSeason])
          : "#draft-scouting-shortlist"
      }
    >
      {isHistoricalScouting
        ? i18n.t("draftScouting.p2s03OpenDraftHistory", {
            ns: "ui",
            defaultValue: "Open draft history",
          })
        : i18n.t("draftScouting.p2v2ReviewShortlist", {
            ns: "ui",
            defaultValue: "Review shortlist",
          })}
    </a>
  );

  const hasEmptyClass = displayedSeasonInfo !== undefined && displayedSeasonInfo.players.length === 0;

  return (
    <div
      data-host-state={isHistoricalScouting ? "readonly" : hasEmptyClass ? "empty" : "ready"}
      data-host-state-host="draftScouting"
      data-host-state-row-count={displayedSeasonInfo?.players.length ?? 0}
    >
      {/* G2d-2: the hub tab strip retired; a single breadcrumb back to the
          converged draft entry replaces it (hidden for freeAgents/noDraft). */}
      {!noDraft ? (
        <a
          className="btn btn-link btn-sm px-0 mb-2 d-inline-flex align-items-center gap-1"
          data-draft-return-link=""
          href={helpers.leagueUrl(["draft"])}
        >
          <span aria-hidden="true">←</span>
          {i18n.t("draftHub.returnToDraft", { ns: "ui", defaultValue: "Back to the draft" })}
        </a>
      ) : null}

      <MobileArchiveSeasonNav
        currentLabel={i18n.t("draftScouting.p2s03SeasonNavLabel", {
          ns: "ui",
          defaultValue: "Scouting season",
        })}
        currentValue={activeSeason}
        nextHref={helpers.leagueUrl(["draft_scouting", activeSeason + 1])}
        nextLabel={i18n.t("draftScouting.nextGroup", { ns: "ui", defaultValue: "Next group" })}
        previousHref={helpers.leagueUrl(["draft_scouting", activeSeason - 1])}
        previousLabel={i18n.t("draftScouting.previousGroup", {
          ns: "ui",
          defaultValue: "Previous group",
        })}
        rootHref={helpers.leagueUrl(["draft_scouting"])}
        rootLabel={i18n.t("draftScouting.p2s03CurrentBoard", {
          ns: "ui",
          defaultValue: "Latest class",
        })}
      />

      <MobileContentHero
        action={heroAction}
        className="mb-3"
        data-mobile-draft-scouting-hero={mobilePreview.confidence}
        explainer={heroExplainer}
        title={heroTitle}
      >
        {/* Historical review: the scout snapshot vs later career outcome is the
            first-screen content ( / review markers). */}
        {isHistoricalScouting ? (
          <MobileStatRow
            label={i18n.t("draftScouting.p2s07ExpectedVsResult", {
              ns: "ui",
              defaultValue: "Expected vs actual",
            })}
            value={i18n.t("draftScouting.p2s03ExpectedActual", {
              ns: "ui",
              defaultValue: "Expected vs actual",
            })}
          />
        ) : null}

        {/* Class overview: coverage, confidence, and lanes preserved as content
            (the P2V2 decision-summary progress/coverage divs → StatRow). */}
        <MobileStatusMetric
          detail={i18n.t("draftScouting.mobilePreviewDesc", {
            ns: "ui",
            count: mobilePreview.previewCount,
            season: mobilePreview.season,
            defaultValue: "Scouts have filed reports on {{count}} prospects in the {{season}} class.",
          })}
          label={i18n.t("draftScouting.p1s08Coverage", {
            ns: "ui",
            defaultValue: "Scout report progress",
          })}
          score={getScoutingConfidenceMetricScore(mobilePreview.confidence)}
          value={scoutingConfidenceLabel}
        />
        <MobileStatRow
          label={i18n.t("draftScouting.p2v2Progress", {
            ns: "ui",
            defaultValue: "Prospects scouted",
          })}
          value={mobileScoutingCoverage}
        />

        {/* Segmented board filter (Overall board / Position fit / Upside). */}
        <div
          className="btn-group btn-group-sm mb-3"
          role="group"
        >
          <a className="btn btn-light-bordered active" href="#draft-scouting-shortlist">
            {isHistoricalScouting
              ? i18n.t("draftScouting.p2s06HistoricalBoard", {
                  ns: "ui",
                  defaultValue: "Historical board",
                })
              : i18n.t("draftScouting.p1s08OverallBoard", {
                  ns: "ui",
                  defaultValue: "Overall board",
                })}
          </a>
          <a className="btn btn-light-bordered" href="#draft-scouting-shortlist">
            {isHistoricalScouting
              ? i18n.t("draftScouting.p2s06DraftSlotReview", {
                  ns: "ui",
                  defaultValue: "Draft slot",
                })
              : i18n.t("draftScouting.p1s08PositionFit", {
                  ns: "ui",
                  defaultValue: "Position fit",
                })}
          </a>
          <a className="btn btn-light-bordered" href="#draft-scouting-shortlist">
            {isHistoricalScouting
              ? i18n.t("draftScouting.p2s06CareerOutcome", {
                  ns: "ui",
                  defaultValue: "Career outcome",
                })
              : i18n.t("draftScouting.p1s08UpsideSegment", {
                  ns: "ui",
                  defaultValue: "Upside",
                })}
          </a>
        </div>

        {hasEmptyClass ? (
          <div>
            <EmptyState
              action={
                <a
                  className="btn btn-outline-secondary"
                  href={helpers.leagueUrl(["draft_history", activeSeason])}
                >
                  {i18n.t("draftScouting.p2s03OpenDraftHistory", {
                    ns: "ui",
                    defaultValue: "Open draft history",
                  })}
                </a>
              }
              reason={isHistoricalScouting ? "empty" : "not-started"}
              title={i18n.t("draftScouting.p2s03EmptyTitle", {
                ns: "ui",
                season: activeSeason,
                defaultValue: "No scouting records for {{season}}",
              })}
            >
              {i18n.t("draftScouting.p2s03EmptyBody", {
                ns: "ui",
                season: activeSeason,
                defaultValue:
                  "This class has no prospects to compare. Use the draft history archive if the class was already completed.",
              })}
            </EmptyState>
          </div>
        ) : null}

        {/* Top prospects are the first-screen content: each recommended rookie
            is a MobileEntityRow (was the P2V2 shortlist card). */}
        {mobileShortlistPlayers.length > 0 ? (
          <section
            className="d-grid gap-2"
          >
            {mobileShortlistPlayers.map((player, index) => (
              <MobileEntityRow
                detail={
                  <>
                    {/* G2d-1 fix: facts only — position and age. The numbered
                        "fit pass" prefix and the repeated per-row instruction
                        line are gone; the static "upside range" chip carried no
                        value and is gone too. */}
                    <div>
                      {isHistoricalScouting
                        ? i18n.t("draftScouting.p2s06HistoricalRookieCardDesc", {
                            ns: "ui",
                            age: player.age,
                            pos: player.pos,
                            defaultValue: "{{pos}}, age {{age}}",
                          })
                        : i18n.t("draftScouting.p2v2ShortlistReason", {
                            ns: "ui",
                            age: player.age,
                            pos: player.pos,
                            defaultValue: "{{pos}}, age {{age}}",
                          })}
                    </div>
                    {isHistoricalScouting ? (
                      <div>
                        {i18n.t("draftScouting.p2s03PlayerExpectedActual", {
                          ns: "ui",
                          actual: player.actualOvr ?? "-",
                          expected: player.ovr,
                          pick:
                            player.draftRound !== undefined && player.draftPick !== undefined
                              ? `${player.draftRound}.${player.draftPick}`
                              : "-",
                          ws: player.careerWs ?? 0,
                          defaultValue:
                            "Expected overall rating {{expected}} → later overall rating {{actual}}; drafted {{pick}}, career win shares {{ws}}.",
                        })}
                      </div>
                    ) : null}
                    {isHistoricalScouting ? (
                      <div className="mt-1 d-flex flex-wrap gap-2">
                        <span className="badge text-bg-light border">
                          {i18n.t("draftScouting.p2s03ReadonlyChip", {
                            ns: "ui",
                            defaultValue: "Read-only snapshot",
                          })}
                        </span>
                      </div>
                    ) : null}
                  </>
                }
                key={player.pid}
                rank={`#${player.rank}`}
                title={formatPlayerName(
                  player.firstName,
                  player.lastName,
                  player.firstNameShort,
                )}
              />
            ))}
          </section>
        ) : null}
      </MobileContentHero>

      {!isHistoricalScouting ? (
        <p>
          <Trans
            i18nKey="draftScouting.description"
            ns="ui"
            defaults="The ratings shown are your scouts' projections for what the players' ratings will be when they enter the {{enterType}}. The further in the future, the more uncertainty there is in their estimates."
            values={{
              enterType: !noDraft
                ? i18n.t("draftScouting.draft", { ns: "ui", defaultValue: "draft" })
                : i18n.t("draftScouting.league", { ns: "ui", defaultValue: "league" }),
            }}
          />
        </p>
      ) : null}

      {pagination ? (
        <div className="d-flex flex-row-reverse">
          <div className="btn-group">
            <button
              className="btn btn-light-bordered"
              disabled={!enablePrevious}
              onClick={() => {
                setPage(page - 1);
              }}
            >
              {i18n.t("draftScouting.previousGroup", {
                ns: "ui",
                defaultValue: "Previous group",
              })}
            </button>
            <button
              className="btn btn-light-bordered"
              disabled={!enableNext}
              onClick={() => {
                setPage(page + 1);
              }}
            >
              {i18n.t("draftScouting.nextGroup", {
                ns: "ui",
                defaultValue: "Next group",
              })}
            </button>
          </div>
        </div>
      ) : null}

      {/* B3: full-table entry follows the P2S expert-depth pattern — a counted
          MobileExpertDisclosure hosts the class DataTables (pruned card lists
          on mobile, unchanged tables on desktop). */}
      <MobileExpertDisclosure
        count={i18n.t("draftScouting.prospectDepthCount", {
          ns: "ui",
          defaultValue: "{{count}} prospects",
          count: seasonsToDisplay.reduce((total, info) => total + info.players.length, 0),
        })}
        data-mobile-draft-scouting-preview={mobilePreview.previewCount}
        data-mobile-draft-scouting-segments={mobilePreview.segments.join(" ")}
        expertHostId="draft-scouting-class-detail"
        id="draft-scouting-shortlist"
        summary={
          isHistoricalScouting
            ? i18n.t("draftScouting.p2s06HistoricalRookieDepth", {
                ns: "ui",
                defaultValue: "Historical rookie board and full class detail",
              })
            : i18n.t("draftScouting.p2v2ProspectDepth", {
                ns: "ui",
                defaultValue: "Prospect shortlist and full class detail",
              })
        }
      >
        <div className="row">
          {seasonsToDisplay.map((info, offset) => {
            return (
              <div key={info.season} className="col-md-4 col-sm-6">
                <DraftClass
                  challengeNoRatings={challengeNoRatings}
                  fantasyDraft={fantasyDraft}
                  offset={offset}
                  players={info.players}
                  season={info.season}
                />
              </div>
            );
          })}
        </div>
      </MobileExpertDisclosure>
    </div>
  );
};

export default DraftScouting;
